import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Check, Loader2, Sparkles } from "lucide-react";
import { seo } from "@/lib/seo";
import { PLANS } from "@/lib/plans";
import { fetchCheckoutPlans, startCashfreeCheckout } from "@/lib/cashfree.functions";
import { openCashfreeCheckout } from "@/lib/cashfree-client";
import {
  formatDuration,
  formatInr,
  PREMIUM_FEATURES,
  SimulationOption,
} from "@/components/billing/premium-lock";
import { useAuth } from "@/hooks/use-auth";
import { useOptionalAuthGate } from "@/components/auth/auth-gate";
import { FREE_LIMITS } from "@/lib/freemium";
import {
  CheckList,
  ClosingCta,
  MarketingPage,
  MarketingSection,
  StartFreeButton,
} from "@/components/marketing/marketing-kit";

export const Route = createFileRoute("/pricing")({
  head: () =>
    seo({
      title: "Pricing & Premium Plans | The Quantum Mind JEE Tracker",
      description:
        "Start free with 2 tests, 2 chapters and 2 question tracker entries. Upgrade to Premium for unlimited JEE syllabus, revision, question and test tracking. Secure payment by Cashfree.",
      path: "/pricing",
    }),
  component: PricingPage,
});

function PricingPage() {
  const { user, loading } = useAuth();
  const gate = useOptionalAuthGate();
  const startCheckout = useServerFn(startCashfreeCheckout);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [simPlans, setSimPlans] = useState<Record<string, boolean>>({});

  const plansQuery = useQuery({
    queryKey: ["checkout-plans"],
    queryFn: () => fetchCheckoutPlans(),
    staleTime: 5 * 60 * 1000,
  });

  const buy = async (planId: string, addonSelected: boolean) => {
    setError(null);
    if (!user && !loading) {
      gate?.openAuthModal();
      return;
    }
    setBusyPlan(planId);
    try {
      const session = await startCheckout({
        data: {
          plan_id: planId,
          simulation_addon_selected: addonSelected,
          payment_type: "new_subscription",
        },
      });
      await openCashfreeCheckout({
        paymentSessionId: session.paymentSessionId,
        mode: session.mode,
      });
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err ?? "");
      setError(
        raw.includes("CASHFREE_NOT_CONFIGURED")
          ? "Payments are being switched on — please try again shortly."
          : "We couldn't open the payment page. Please try again.",
      );
      setBusyPlan(null);
    }
  };

  const plans = plansQuery.data?.plans ?? [];

  return (
    <MarketingPage>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Pricing
      </p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
        Start free, upgrade when you outgrow it
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
        Every tracker works on the free plan with a small starter quota. Premium removes the limits
        and unlocks the full engine. Payments are handled securely by Cashfree.
      </p>

      <MarketingSection title="Free plan">
        <div className="glass rounded-3xl border border-hairline p-6">
          <h2 className="text-[16px] font-bold tracking-tight">{PLANS.free.name}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{PLANS.free.tagline}</p>
          <p className="mt-4 text-3xl font-extrabold tracking-tight">₹0</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Includes {FREE_LIMITS.tests} tests, {FREE_LIMITS.chapters} chapters and{" "}
            {FREE_LIMITS.questionChapters} question tracker entries.
          </p>
          <div className="mt-5">
            <CheckList items={PLANS.free.features} />
          </div>
          <StartFreeButton className="mt-6 w-full" label="Create free account" />
        </div>
      </MarketingSection>

      <MarketingSection title="Premium plans">
        <div className="glass rounded-3xl border border-hairline p-6">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
            <Sparkles className="size-3.5" aria-hidden /> Premium
          </p>
          <ul className="mt-4 space-y-2">
            {PREMIUM_FEATURES.map((item) => (
              <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                <Check className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 space-y-2">
            {plansQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden /> Loading plans…
              </div>
            ) : plans.length > 0 ? (
              plans.map((plan) => {
                const addonSelected = simPlans[plan.id] === true;
                const total = plan.basePaise + (addonSelected ? plan.addonPaise : 0);
                return (
                  <div
                    key={plan.id}
                    className="rounded-2xl border border-hairline bg-card/60 px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold tracking-tight">{plan.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {formatDuration(plan.duration) || "Subscription"}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-base font-extrabold">{formatInr(total)}</span>
                        <button
                          type="button"
                          disabled={busyPlan !== null}
                          onClick={() => void buy(plan.id, addonSelected)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition active:scale-[0.98] disabled:opacity-60"
                        >
                          {busyPlan === plan.id ? (
                            <Loader2 className="size-3.5 animate-spin" aria-hidden />
                          ) : null}
                          Upgrade
                        </button>
                      </div>
                    </div>
                    <SimulationOption
                      plan={plan}
                      checked={addonSelected}
                      disabled={busyPlan !== null}
                      onChange={(next: boolean) =>
                        setSimPlans((prev) => ({ ...prev, [plan.id]: next }))
                      }
                    />
                  </div>
                );
              })
            ) : (
              <p className="py-4 text-center text-xs text-muted-foreground">
                Plans are unavailable right now. Please try again in a moment.
              </p>
            )}
          </div>

          {error ? (
            <p role="alert" className="mt-3 text-xs font-medium text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      </MarketingSection>

      <MarketingSection title="Billing questions">
        <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
          <p>
            <strong className="text-foreground">Do I need a card to start?</strong> No. The free
            plan needs only an email address or a Google account.
          </p>
          <p>
            <strong className="text-foreground">What happens to my data if I stay free?</strong>{" "}
            Nothing is deleted. Your existing chapters, revisions, tests and scores stay exactly as
            they are — the free quota only limits creating new ones.
          </p>
          <p>
            <strong className="text-foreground">How are payments processed?</strong> Through
            Cashfree's secure hosted checkout (UPI, cards, net banking, wallets). Card details never
            touch this app, and premium is activated only after Cashfree confirms the payment.
          </p>
        </div>
      </MarketingSection>

      <ClosingCta
        title="Start with the free plan"
        body="Create an account and keep your progress synced across devices."
      />
    </MarketingPage>
  );
}
